import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import {
  Heart,
  Star,
  Trophy,
  Zap,
  Sparkles,
  Target,
  TrendingUp,
  CheckCircle2,
  Rocket,
  Camera,
  Video,
  Share2
} from 'lucide-react';

// Hook per rispettare prefers-reduced-motion
const usePrefersReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return prefersReducedMotion;
};

// Hook per rilevare mobile
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
};

// Confetti Component per celebrazioni
export const ConfettiExplosion: React.FC<{ trigger: boolean; onComplete?: () => void }> = ({
  trigger,
  onComplete
}) => {
  const [particles, setParticles] = useState<Array<{ id: number; color: string; x: number; y: number }>>([]);
  const prefersReducedMotion = usePrefersReducedMotion();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (trigger && !prefersReducedMotion) {
      const colors = ['#FEF08A', '#FDE047', '#FACC15', '#EAB308', '#CA8A04'];
      // Reduce particles on mobile for better performance
      const particleCount = isMobile ? 15 : 30;
      const newParticles = Array(particleCount).fill(null).map((_, i) => ({
        id: i,
        color: colors[Math.floor(Math.random() * colors.length)],
        x: Math.random() * 100,
        y: Math.random() * 100
      }));
      setParticles(newParticles);

      setTimeout(() => {
        setParticles([]);
        onComplete?.();
      }, 3000);
    } else if (trigger && prefersReducedMotion) {
      // Just call onComplete without animation
      setTimeout(() => onComplete?.(), 500);
    }
  }, [trigger, onComplete, prefersReducedMotion, isMobile]);

  if (prefersReducedMotion) return null;

  return (
    <AnimatePresence>
      {particles.length > 0 && (
        <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
          {particles.map((particle) => (
            <motion.div
              key={particle.id}
              className="absolute w-2 h-2 sm:w-2 sm:h-2 rounded-full"
              style={{ backgroundColor: particle.color }}
              initial={{
                x: '50vw',
                y: '50vh',
                scale: 0,
                rotate: 0
              }}
              animate={{
                x: `${particle.x}vw`,
                y: `${particle.y}vh`,
                scale: [0, 1, 0],
                rotate: 360
              }}
              transition={{
                duration: 3,
                ease: 'easeOut'
              }}
            />
          ))}
        </div>
      )}
    </AnimatePresence>
  );
};

// Success Celebration Component
export const SuccessCelebration: React.FC<{
  show: boolean;
  title: string;
  message: string;
  icon?: React.ReactNode;
  onClose: () => void;
}> = ({ show, title, message, icon, onClose }) => {
  const [showConfetti, setShowConfetti] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (show && !prefersReducedMotion) {
      setShowConfetti(true);
    }
  }, [show, prefersReducedMotion]);

  return (
    <>
      <ConfettiExplosion trigger={showConfetti} onComplete={() => setShowConfetti(false)} />
      <AnimatePresence>
        {show && (
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4"
            initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          >
            <motion.div
              className="bg-white dark:bg-gray-800 rounded-2xl p-5 sm:p-8 max-w-[95vw] sm:max-w-sm w-full text-center shadow-2xl"
              initial={prefersReducedMotion ? { opacity: 1 } : { scale: 0.8, y: 50 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={prefersReducedMotion ? { opacity: 0 } : { scale: 0.8, y: 50 }}
              transition={prefersReducedMotion ? { duration: 0.1 } : { type: 'spring', damping: 20, stiffness: 300 }}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <motion.div
                className="mx-auto w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full flex items-center justify-center mb-3 sm:mb-4"
                initial={prefersReducedMotion ? {} : { scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={prefersReducedMotion ? { duration: 0 } : { delay: 0.2, type: 'spring', damping: 15 }}
              >
                {icon || <Trophy className="w-6 h-6 sm:w-8 sm:h-8 text-white" />}
              </motion.div>

              <motion.h3
                className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100 mb-2"
                initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: prefersReducedMotion ? 0 : 0.3 }}
              >
                {title}
              </motion.h3>

              <motion.p
                className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-5 sm:mb-6 px-2"
                initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: prefersReducedMotion ? 0 : 0.4 }}
              >
                {message}
              </motion.p>

              <motion.button
                className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 px-6 py-3 min-h-[48px] sm:min-h-[44px] rounded-xl font-medium hover:from-yellow-500 hover:to-yellow-600 active:scale-95 transition-all shadow-lg touch-manipulation w-full sm:w-auto"
                onClick={onClose}
                whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
                whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
                initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: prefersReducedMotion ? 0 : 0.5 }}
              >
                Fantastico!
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Floating Action Button with delight
export const DelightfulFAB: React.FC<{
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  className?: string;
}> = ({ onClick, icon, label, className = '' }) => {
  const [isPressed, setIsPressed] = useState(false);
  const [showRipple, setShowRipple] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();

  const handleClick = () => {
    if (!prefersReducedMotion) {
      setShowRipple(true);
      setTimeout(() => setShowRipple(false), 600);
    }
    onClick();
  };

  return (
    <motion.button
      className={`relative fixed bottom-20 sm:bottom-6 right-4 sm:right-6 w-14 h-14 sm:w-14 sm:h-14 min-w-[56px] min-h-[56px] bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full shadow-lg flex items-center justify-center text-gray-900 overflow-hidden touch-manipulation ${className}`}
      onClick={handleClick}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      onMouseLeave={() => setIsPressed(false)}
      onTouchStart={() => setIsPressed(true)}
      onTouchEnd={() => setIsPressed(false)}
      whileHover={prefersReducedMotion ? {} : { scale: 1.1, rotate: 5 }}
      whileTap={prefersReducedMotion ? {} : { scale: 0.9 }}
      initial={prefersReducedMotion ? { opacity: 1 } : { scale: 0, rotate: -180 }}
      animate={{ scale: 1, rotate: 0, opacity: 1 }}
      transition={prefersReducedMotion ? { duration: 0 } : { type: 'spring', damping: 15 }}
      aria-label={label}
    >
      <motion.div
        animate={prefersReducedMotion ? {} : { rotate: isPressed ? 180 : 0 }}
        transition={{ duration: 0.3 }}
      >
        {icon}
      </motion.div>

      {/* Ripple Effect - disabled with reduced motion */}
      {!prefersReducedMotion && (
        <AnimatePresence>
          {showRipple && (
            <motion.div
              className="absolute inset-0 bg-white rounded-full"
              initial={{ scale: 0, opacity: 0.6 }}
              animate={{ scale: 4, opacity: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6 }}
            />
          )}
        </AnimatePresence>
      )}
    </motion.button>
  );
};

// Progress Ring con animazioni deliziose
export const DelightfulProgressRing: React.FC<{
  progress: number;
  size?: number;
  strokeWidth?: number;
  children?: React.ReactNode;
  responsive?: boolean;
}> = ({ progress, size = 120, strokeWidth = 8, children, responsive = true }) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const isMobile = useIsMobile();

  // Responsive size adjustments
  const actualSize = responsive && isMobile ? Math.min(size, 100) : size;
  const actualStrokeWidth = responsive && isMobile ? Math.max(strokeWidth - 2, 4) : strokeWidth;

  const radius = (actualSize - actualStrokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div className="relative flex items-center justify-center">
      <svg width={actualSize} height={actualSize} className="transform -rotate-90">
        {/* Background Circle */}
        <circle
          cx={actualSize / 2}
          cy={actualSize / 2}
          r={radius}
          stroke="#e5e7eb"
          strokeWidth={actualStrokeWidth}
          fill="transparent"
          className="opacity-20 dark:opacity-30"
        />

        {/* Progress Circle */}
        <motion.circle
          cx={actualSize / 2}
          cy={actualSize / 2}
          r={radius}
          stroke="url(#progressGradient)"
          strokeWidth={actualStrokeWidth}
          fill="transparent"
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: prefersReducedMotion ? strokeDashoffset : circumference }}
          animate={{ strokeDashoffset }}
          transition={prefersReducedMotion ? { duration: 0 } : { duration: 1.5, ease: 'easeOut' }}
        />

        {/* Gradient Definition */}
        <defs>
          <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FEF08A" />
            <stop offset="50%" stopColor="#FDE047" />
            <stop offset="100%" stopColor="#EAB308" />
          </linearGradient>
        </defs>
      </svg>

      {/* Center Content */}
      <div className="absolute inset-0 flex items-center justify-center">
        {children || (
          <motion.div
            className="text-center"
            initial={prefersReducedMotion ? {} : { scale: 0 }}
            animate={{ scale: 1 }}
            transition={prefersReducedMotion ? { duration: 0 } : { delay: 0.5, type: 'spring' }}
          >
            <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">{Math.round(progress)}%</div>
            <div className="text-[10px] sm:text-xs text-gray-600 dark:text-gray-400">Completato</div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

// Card con hover delight
export const DelightfulCard: React.FC<{
  children: React.ReactNode;
  className?: string;
  hoverScale?: number;
  delay?: number;
}> = ({ children, className = '', hoverScale = 1.02, delay = 0 }) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const isMobile = useIsMobile();

  return (
    <motion.div
      className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer ${className}`}
      initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={prefersReducedMotion ? { duration: 0 } : { delay, duration: 0.5 }}
      whileHover={prefersReducedMotion || isMobile ? {} : {
        scale: hoverScale,
        y: -4,
        transition: { duration: 0.2 }
      }}
      whileTap={prefersReducedMotion ? {} : { scale: 0.98 }}
    >
      {children}
    </motion.div>
  );
};

// Loading con personalita
export const DelightfulLoading: React.FC<{
  messages?: string[];
  duration?: number;
}> = ({
  messages = [
    "Creando magia video...",
    "Mescolando creativita...",
    "Ottimizzando per i social...",
    "Quasi pronto per il pubblico!"
  ],
  duration = 2000
}) => {
  const [currentMessage, setCurrentMessage] = useState(0);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMessage((prev) => (prev + 1) % messages.length);
    }, duration);

    return () => clearInterval(interval);
  }, [messages.length, duration]);

  return (
    <div className="flex flex-col items-center space-y-4 p-6 sm:p-8">
      <motion.div
        className="relative"
        animate={prefersReducedMotion ? {} : { rotate: 360 }}
        transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
      >
        <div className="w-14 h-14 sm:w-16 sm:h-16 border-4 border-yellow-200 border-t-yellow-500 rounded-full" />
        <motion.div
          className="absolute inset-2 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full flex items-center justify-center"
          animate={prefersReducedMotion ? {} : { scale: [1, 1.1, 1] }}
          transition={{ duration: 1, repeat: Infinity }}
        >
          <Camera className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
        </motion.div>
      </motion.div>

      <AnimatePresence mode="wait">
        <motion.p
          key={currentMessage}
          className="text-sm sm:text-base text-gray-700 dark:text-gray-300 font-medium text-center max-w-[250px] sm:max-w-none"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={prefersReducedMotion ? {} : { opacity: 0, y: -10 }}
          transition={{ duration: prefersReducedMotion ? 0 : 0.3 }}
        >
          {messages[currentMessage]}
        </motion.p>
      </AnimatePresence>

      <div className="flex space-x-1.5 sm:space-x-1">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="w-2.5 h-2.5 sm:w-2 sm:h-2 bg-yellow-400 rounded-full"
            animate={prefersReducedMotion ? {} : { scale: [1, 1.5, 1] }}
            transition={{
              duration: 0.6,
              repeat: Infinity,
              delay: i * 0.2
            }}
          />
        ))}
      </div>
    </div>
  );
};

// Empty State con personalita
export const DelightfulEmptyState: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  actionText?: string;
  onAction?: () => void;
  illustration?: string;
}> = ({ icon, title, description, actionText, onAction, illustration }) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const isMobile = useIsMobile();

  return (
    <motion.div
      className="text-center py-8 sm:py-12 px-4 sm:px-6"
      initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.5 }}
    >
      <motion.div
        className="mx-auto w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-r from-yellow-100 to-yellow-200 dark:from-yellow-900/30 dark:to-yellow-800/30 rounded-full flex items-center justify-center mb-4 sm:mb-6"
        whileHover={prefersReducedMotion || isMobile ? {} : {
          scale: 1.1,
          rotate: [0, -10, 10, 0],
          transition: { duration: 0.5 }
        }}
      >
        <div className="text-yellow-600">
          {icon}
        </div>
      </motion.div>

      <motion.h3
        className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100 mb-2"
        initial={prefersReducedMotion ? {} : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: prefersReducedMotion ? 0 : 0.2 }}
      >
        {title}
      </motion.h3>

      <motion.p
        className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-5 sm:mb-6 max-w-md mx-auto px-2"
        initial={prefersReducedMotion ? {} : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: prefersReducedMotion ? 0 : 0.3 }}
      >
        {description}
      </motion.p>

      {actionText && onAction && (
        <motion.button
          className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 px-6 py-3 min-h-[48px] sm:min-h-[44px] rounded-xl font-medium hover:from-yellow-500 hover:to-yellow-600 active:scale-95 transition-all shadow-lg touch-manipulation w-full sm:w-auto"
          onClick={onAction}
          whileHover={prefersReducedMotion ? {} : { scale: 1.05, y: -2 }}
          whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: prefersReducedMotion ? 0 : 0.4 }}
        >
          {actionText}
        </motion.button>
      )}
    </motion.div>
  );
};

// Toast notifications con personalita
export const DelightfulToast: React.FC<{
  show: boolean;
  type: 'success' | 'error' | 'info' | 'warning';
  title: string;
  message: string;
  onClose: () => void;
}> = ({ show, type, title, message, onClose }) => {
  const prefersReducedMotion = usePrefersReducedMotion();

  const getIcon = () => {
    switch (type) {
      case 'success': return <CheckCircle2 className="w-5 h-5" />;
      case 'error': return <Zap className="w-5 h-5" />;
      case 'warning': return <Target className="w-5 h-5" />;
      default: return <Sparkles className="w-5 h-5" />;
    }
  };

  const getColors = () => {
    switch (type) {
      case 'success': return 'from-green-400 to-green-500 text-white';
      case 'error': return 'from-red-400 to-red-500 text-white';
      case 'warning': return 'from-orange-400 to-orange-500 text-white';
      default: return 'from-blue-400 to-blue-500 text-white';
    }
  };

  useEffect(() => {
    if (show) {
      const timer = setTimeout(onClose, 4000);
      return () => clearTimeout(timer);
    }
  }, [show, onClose]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className={`fixed top-4 sm:top-4 left-2 right-2 sm:left-auto sm:right-4 mx-auto sm:mx-0 max-w-[calc(100vw-16px)] sm:max-w-sm w-full bg-gradient-to-r ${getColors()} rounded-xl shadow-lg p-3 sm:p-4 z-50`}
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, x: 300, scale: 0.8 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, x: 300, scale: 0.8 }}
          transition={prefersReducedMotion ? { duration: 0.1 } : { type: 'spring', damping: 20 }}
        >
          <div className="flex items-start space-x-3">
            <motion.div
              initial={prefersReducedMotion ? {} : { scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={prefersReducedMotion ? { duration: 0 } : { delay: 0.2, type: 'spring' }}
              className="flex-shrink-0 mt-0.5"
            >
              {getIcon()}
            </motion.div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm sm:text-base">{title}</p>
              <p className="text-xs sm:text-sm opacity-90 line-clamp-2">{message}</p>
            </div>
            <button
              onClick={onClose}
              className="opacity-70 hover:opacity-100 transition-opacity p-1 -m-1 touch-manipulation min-w-[32px] min-h-[32px] flex items-center justify-center flex-shrink-0"
              aria-label="Chiudi"
            >
              <span className="text-xl leading-none">x</span>
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Export hooks for reuse
export { usePrefersReducedMotion, useIsMobile };
